import { useState } from 'react';
import { Link } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';
import SEOJsonLd from '@/components/base/SEOJsonLd';

const stats = [
  { value: '70%', label: 'Repeat Order Rate' },
  { value: '250+', label: 'Brand Partners' },
  { value: '1600+', label: 'Formula Database' },
  { value: '7', label: 'Dosage Form Types' },
];

const products = [
  { name: 'Multi-Vitamin Capsules', category: 'Hard Capsule', img: 'https://readdy.ai/api/search-image?query=health%20supplement%20capsule%20bottle%20vitamin%20complex%20product%20on%20clean%20white%20background%2C%20professional%20commercial%20product%20photography%2C%20green%20label%2C%20minimalist%20style&width=400&height=400&seq=product-01&orientation=squarish' },
  { name: 'Probiotic Powder Sachet', category: 'Powder Form', img: 'https://readdy.ai/api/search-image?query=probiotic%20powder%20sachet%20health%20supplement%20packet%20product%20on%20white%20background%2C%20clean%20professional%20product%20photography%2C%20green%20packaging%2C%20minimalist&width=400&height=400&seq=product-02&orientation=squarish' },
  { name: 'Collagen Drink', category: 'Beverage Form', img: 'https://readdy.ai/api/search-image?query=collagen%20drink%20bottle%20health%20beverage%20supplement%20on%20clean%20white%20background%2C%20professional%20commercial%20photography%2C%20elegant%20packaging%2C%20light%20green%20tones&width=400&height=400&seq=product-03&orientation=squarish' },
  { name: 'Herbal Tablets', category: 'Tablet Form', img: 'https://readdy.ai/api/search-image?query=herbal%20plant%20extract%20tablet%20supplement%20bottle%20on%20white%20background%2C%20professional%20product%20photography%2C%20natural%20green%20packaging%2C%20clean%20minimal%20style&width=400&height=400&seq=product-04&orientation=squarish' },
  { name: 'Fish Oil Soft Gels', category: 'Soft Gel Form', img: 'https://readdy.ai/api/search-image?query=fish%20oil%20omega3%20softgel%20capsule%20supplement%20bottle%20on%20white%20background%2C%20professional%20health%20product%20photography%2C%20clean%20minimal%20packaging%2C%20golden%20soft%20capsules%20visible&width=400&height=400&seq=product-05&orientation=squarish' },
];

const processSteps = [
  { step: '01', icon: 'ri-discuss-line', title: 'Needs Consultation', desc: 'Discuss your product needs, target market and budget with our specialist team to define the collaboration direction.' },
  { step: '02', icon: 'ri-draft-line', title: 'Formula Proposal', desc: 'Our R&D team recommends a formula based on your requirements, including ingredient selection, function focus and regulatory review.' },
  { step: '03', icon: 'ri-palette-line', title: 'Packaging Design', desc: 'We provide packaging specification recommendations, confirm container selection, label layout, and review label copy for regulatory compliance.' },
  { step: '04', icon: 'ri-file-check-line', title: 'Testing & Certification', desc: 'Samples undergo third-party safety testing with complete CoA reports issued. We assist with certification application documentation.' },
  { step: '05', icon: 'ri-settings-3-line', title: 'Mass Production Confirmation', desc: 'After sample approval and contract signing, full-scale production begins under GMP quality monitoring at every stage.' },
  { step: '06', icon: 'ri-truck-line', title: 'Delivery Completed', desc: 'After QC acceptance, products are shipped on schedule with complete batch test reports. Ongoing technical support provided.' },
];

const testimonials = [
  {
    name: 'Mr. Chen, Brand Founder',
    company: 'Mr. Health Brand',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20businessman%20portrait%20headshot%20confident%20smiling%2C%20clean%20white%20background%2C%20business%20casual%20attire%2C%2040s%20male&width=120&height=120&seq=avatar-01&orientation=squarish',
    quote: 'Kang Yong helped us build our health supplement brand from scratch — from formula development to packaging design. We launched two months ahead of schedule.',
    rating: 5,
  },
  {
    name: 'Ms. Lin, Product Manager',
    company: 'Natural Origin Brand',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20businesswoman%20portrait%20headshot%20warm%20smiling%2C%20clean%20white%20background%2C%20business%20casual%20attire%2C%2035s%20female&width=120&height=120&seq=avatar-02&orientation=squarish',
    quote: "We've been working with Kang Yong for over three years. The quality is always consistent, and their regulatory documentation is excellent — which saved us a lot of headaches when expanding overseas.",
    rating: 5,
  },
  {
    name: 'Mr. Wang, General Manager',
    company: 'VitalSource Biotech',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20executive%20manager%20portrait%20headshot%20serious%20confident%2C%20clean%20white%20background%2C%20formal%20business%20attire%2C%2050s%20male&width=120&height=120&seq=avatar-03&orientation=squarish',
    quote: "Kang Yong's R&D team truly understands our market positioning and always recommends the most suitable formula. They're a manufacturing partner that genuinely puts clients first.",
    rating: 5,
  },
];

const faqs = [
  { q: 'What is the minimum order quantity (MOQ)?', a: 'MOQ varies by dosage form and formula complexity. For standard capsule and tablet products, the typical MOQ starts from 3,000–5,000 bottles. Please contact us for a specific evaluation based on your product.' },
  { q: 'Can you develop a custom formula for us?', a: 'Absolutely. Kang Yong Biotech has a dedicated in-house R&D team that can develop fully customized formulas based on your functional requirements, target audience and market positioning.' },
  { q: 'How long does it take from formula confirmation to production completion?', a: 'Generally, 2–4 weeks from formula confirmation to sample completion; after sample approval, mass production takes approximately 4–8 weeks depending on order volume.' },
  { q: 'Do you offer packaging design services?', a: 'Yes. We provide label design and packaging consulting services. We can also work with your own design files. Please discuss your packaging needs with our team.' },
  { q: 'Do your products comply with Taiwan and export regulations?', a: 'We are well-versed in Taiwan health food regulations and have extensive export experience. We can assist with product label review and regulatory submissions to ensure compliant market launch.' },
  { q: 'How do I get started?', a: 'Contact us via our website inquiry form, phone or LINE. Our team will respond promptly and arrange a detailed needs consultation.' },
];

export default function EnHomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useSEO({
    title: 'Kang Yong Biotech | Health Supplement OEM ODM Manufacturing Taiwan',
    description: 'Kang Yong Biotech offers professional health supplement OEM/ODM manufacturing in Taiwan. From formula R&D to packaging and delivery — your trusted manufacturing partner.',
    keywords: 'health supplement OEM, ODM manufacturing Taiwan, supplement contract manufacturing, HACCP certified factory',
    lang: 'en',
    canonical: 'https://kangyong.com/en',
  });

  const siteUrl = (window as any).__VITE_SITE_URL__ || 'https://kangyong.com';

  return (
    <EnLayout>
      <SEOJsonLd type="WebSite" />
      <SEOJsonLd type="Organization" pageTitle="Kang Yong Biotech | Health Supplement OEM ODM Manufacturing Taiwan" pageDescription="Kang Yong Biotech offers professional health supplement OEM/ODM manufacturing in Taiwan." pageUrl={`${siteUrl}/en`} />
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=lush%20green%20nature%20botanical%20herbs%20plants%20background%20with%20soft%20light%20rays%20through%20leaves%2C%20fresh%20healthy%20organic%20feel%2C%20blurred%20background%20with%20vibrant%20green%20tones%2C%20minimalist%20clean%20aesthetic%20for%20biotech%20health%20supplement%20brand&width=1920&height=1080&seq=hero-kangyong-01&orientation=landscape"
            alt="Kang Yong Biotech Hero Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/30 to-black/10" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 py-28 pb-20 lg:py-0">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#4CAF50]/20 border border-[#4CAF50]/50 text-[#7ed87f] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-6 tracking-wide">
              Professional OEM / ODM Manufacturing
            </span>
            <h1 className="text-white font-bold leading-tight mb-6">
              <span className="block text-[2rem] md:text-[2.75rem] leading-[1.25]">Kang Yong Biotech</span>
              <span className="block text-[2rem] md:text-[2.75rem] leading-[1.25]">OEM / ODM Services</span>
              <span className="block text-[1.25rem] md:text-[1.625rem] font-medium text-green-300 mt-2">Your Most Trusted Manufacturing Partner</span>
            </h1>
            <p className="text-gray-200 text-[1.125rem] leading-[1.75] mb-10 max-w-xl">
              From formula development and production to packaging and delivery — we provide end-to-end OEM/ODM manufacturing for health supplements and biotech products, helping you launch faster.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <Link to="/en/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1.0625rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
                Start Your Inquiry
              </Link>
              <Link to="/en/services" className="whitespace-nowrap bg-white/15 hover:bg-white/25 border border-white/60 text-white font-semibold text-[1.0625rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
                Our Services
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10 animate-bounce">
          <span className="text-white/50 text-[0.8125rem] tracking-widest">SCROLL</span>
          <i className="ri-arrow-down-line text-white/50 text-xl" />
        </div>
      </section>

      {/* Service Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 tracking-wide border border-green-200">
              Our Services
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">Professional OEM/ODM Manufacturing Services</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Kang Yong Biotech provides comprehensive health supplement and biotech product manufacturing services, helping brands bring products to market efficiently.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-flask-line', title: 'Formula R&D', desc: 'Our professional R&D team develops unique formulas tailored to your needs, combining the latest ingredient trends and formulation technology.', tag: 'R&D Services', color: '#4CAF50' },
              { icon: 'ri-capsule-line', title: 'Dosage Form Design', desc: 'Select the most suitable dosage form based on ingredient properties and product requirements, ensuring high product stability and shelf life.', tag: 'Manufacturing', color: '#66BB6A' },
              { icon: 'ri-award-line', title: 'Packaging Design', desc: 'Custom packaging design services to create a distinctive look for your products, helping your brand stand out in the market.', tag: 'Design Services', color: '#81C784' },
              { icon: 'ri-truck-line', title: 'Testing & Certification', desc: 'Assist with third-party testing and certification to ensure product safety and compliance for a smooth market launch.', tag: 'Additional Services', color: '#A5D6A7' },
            ].map((s) => (
              <div key={s.title} className="bg-white border border-gray-100 rounded-2xl p-7 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 cursor-default group">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl mb-5" style={{ backgroundColor: `${s.color}20` }}>
                  <i className={`${s.icon} text-2xl`} style={{ color: s.color }} />
                </div>
                <span className="inline-block text-[0.75rem] font-medium px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${s.color}18`, color: s.color }}>{s.tag}</span>
                <h3 className="text-[#1a2e1a] font-bold text-[1.1875rem] mb-3">{s.title}</h3>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/en/services" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1rem] px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
              View All Services
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row items-center gap-14 lg:gap-20">
            <div className="w-full lg:w-1/2 flex-shrink-0">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20biotech%20laboratory%20scientist%20researcher%20working%20with%20natural%20herb%20ingredients%20in%20modern%20clean%20lab%20environment%2C%20green%20plants%20bottles%20supplements%20on%20desk%2C%20bright%20natural%20lighting%2C%20high%20quality%20commercial%20photography%20for%20health%20food%20brand&width=720&height=540&seq=about-preview-01&orientation=landscape"
                  alt="Kang Yong Biotech Lab"
                  className="w-full h-[260px] sm:h-[340px] lg:h-[440px] object-cover object-top rounded-2xl"
                />
                <div className="absolute bottom-6 left-6 bg-white rounded-xl px-5 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-lg">
                    <i className="ri-shield-check-line text-[#4CAF50] text-xl" />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-bold text-[1rem]">HACCP Certified Facility</p>
                    <p className="text-gray-400 text-[0.8125rem]">Meets international manufacturing standards</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">About Kang Yong</span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] md:text-[2.25rem] leading-tight mb-6">Your Best Partner for Health Supplement Manufacturing</h2>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                Kang Yong Biotech has been dedicated to OEM/ODM manufacturing for health supplements and biotech products for many years. We have comprehensive R&D, production and quality management processes to help brands turn ideas into market-ready products.
              </p>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                From raw material sourcing to finished product shipment, every step is strictly monitored to ensure your products meet market regulations and launch safely.
              </p>
              <div className="grid grid-cols-2 gap-5 mb-10">
                {[
                  { icon: 'ri-building-line', label: 'HACCP Certified Facility' },
                  { icon: 'ri-test-tube-line', label: 'In-House R&D Lab' },
                  { icon: 'ri-global-line', label: 'Professional Manufacturing Services' },
                  { icon: 'ri-customer-service-line', label: 'Dedicated Account Support' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#4CAF50] text-lg`} />
                    </div>
                    <span className="text-[#1a2e1a] font-medium text-[0.9375rem]">{item.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/en/about" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
                Learn More <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#2d5a2d]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-white font-bold text-[2rem] md:text-[2.75rem] xl:text-[3.25rem] leading-none mb-2">{stat.value}</p>
                <p className="text-green-200 text-[1rem] leading-[1.75]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Product Examples</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] md:text-[2.25rem] mb-4">Diverse Dosage Forms to Meet Market Demand</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">We offer a wide range of dosage forms, tailored to your brand requirements to help you expand your product line.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {products.map((p) => (
              <div
                key={p.name}
                className="w-[calc(50%-0.375rem)] sm:w-[calc(33.333%-0.667rem)] lg:w-[calc(20%-0.8rem)] bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 group cursor-default"
              >
                <div className="w-full h-[200px] sm:h-[220px] overflow-hidden">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-3 text-center">
                  <span className="text-[#4CAF50] text-[0.75rem] font-medium">{p.category}</span>
                  <p className="text-[#1a2e1a] font-semibold text-[0.875rem] mt-1">{p.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Six Core Advantages
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">Six Core Advantages</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              We earn the trust of hundreds of brands through professional expertise, rigorous quality management and comprehensive service.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-microscope-line', title: 'Professional R&D Team', desc: 'In-house R&D laboratory staffed by food science and biotechnology experts, continuously tracking the latest ingredient trends and formulation technologies.' },
              { icon: 'ri-verified-badge-line', title: 'Quality Certification Assurance', desc: 'HACCP and ISO 22000 certified. Every production batch comes with a complete quality inspection report for your peace of mind.' },
              { icon: 'ri-time-line', title: 'Fast & Reliable Lead Times', desc: 'Optimized production scheduling efficiently shortens the timeline from sample confirmation to mass production and market launch.' },
              { icon: 'ri-settings-3-line', title: 'Flexible Customization', desc: 'From formula, dosage form, packaging to labeling — full customization to match your brand positioning and channel requirements.' },
              { icon: 'ri-file-list-3-line', title: 'Regulatory Compliance Expertise', desc: 'Deep familiarity with Taiwan and international health supplement regulations to ensure your products launch fully compliant from day one.' },
              { icon: 'ri-clipboard-line', title: 'Testing & Certification Support', desc: 'We assist with third-party testing, health food certification applications, and all required documentation for your product registration needs.' },
            ].map((item) => (
              <div key={item.title} className="flex gap-5 p-6 rounded-2xl bg-[#f7faf7] hover:bg-green-50 border border-transparent hover:border-green-200 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl flex-shrink-0 border border-green-100">
                  <i className={`${item.icon} text-[#4CAF50] text-xl`} />
                </div>
                <div>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Cooperation Process</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] md:text-[2.25rem] mb-4">Six Steps to Start Collaboration</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">Kang Yong Biotech provides a clear and transparent cooperation process so you can move forward with confidence.</p>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-green-100 z-0" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 relative z-10">
              {processSteps.map((s, idx) => (
                <div key={s.step} className="flex flex-col items-center text-center">
                  <div className="relative mb-5">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-white border-2 border-[#4CAF50]">
                      <i className={`${s.icon} text-[#4CAF50] text-2xl`} />
                    </div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">{idx + 1}</span>
                  </div>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-12">
            <Link to="/en/partnership" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
              View Partnership Details <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Client Testimonials</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] md:text-[2.25rem] mb-4">Brands That Choose Kang Yong</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">Trusted by 250+ brands — our reputation is built on results.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-[#f7faf7] rounded-2xl p-7 border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#FFCA28] text-base" />
                  ))}
                </div>
                <p className="text-gray-600 text-[0.9375rem] leading-[1.75] mb-6 italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-semibold text-[0.9375rem]">{t.name}</p>
                    <p className="text-gray-400 text-[0.8125rem]">{t.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-14 lg:gap-20 items-start">
            <div className="w-full lg:w-1/3 flex-shrink-0">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">FAQ</span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] md:text-[2.25rem] leading-tight mb-6">Frequently Asked Questions</h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] mb-8">Can&apos;t find what you&apos;re looking for? Contact our team directly.</p>
              <Link to="/en/contact" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer">
                Contact Us <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="w-full lg:w-2/3">
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <div key={idx} className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${openFaq === idx ? 'border-[#4CAF50]/40' : 'border-gray-100'}`}>
                    <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}>
                      <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openFaq === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>{faq.q}</span>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${openFaq === idx ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                        <i className={`text-sm transition-transform duration-200 ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </div>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-600 text-[0.9375rem] leading-[1.75]">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=lush%20tropical%20green%20forest%20leaves%20background%20with%20soft%20sunlight%20bokeh%20effect%2C%20vibrant%20emerald%20green%20nature%20scene%2C%20botanical%20garden%20aesthetic%2C%20health%20wellness%20brand%20background%2C%20rich%20green%20tones&width=1920&height=600&seq=cta-banner-bg-01&orientation=landscape"
            alt="CTA Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#2d5a2d]/80" />
        </div>
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="text-center lg:text-left w-full lg:w-auto">
              <h2 className="text-white font-bold text-[1.875rem] md:text-[2.25rem] leading-tight mb-4">Ready to Start Your Brand Manufacturing Journey?</h2>
              <p className="text-green-200 text-[1.0625rem] leading-[1.75] max-w-xl">Contact the Kang Yong Biotech team today. Let us create a tailored OEM/ODM solution that brings your brand vision to life.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0 w-full lg:w-auto">
              <Link to="/en/contact/form" className="whitespace-nowrap bg-white text-[#2d5a2d] hover:bg-green-50 font-bold text-[1.0625rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer text-center">
                Start Your Inquiry
              </Link>
              <Link to="/en/services" className="whitespace-nowrap bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold text-[1.0625rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer text-center">
                Browse Services
              </Link>
            </div>
          </div>
          <div className="mt-12 pt-10 border-t border-white/20 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: 'ri-shield-check-line', text: 'HACCP Certified Facility' },
              { icon: 'ri-award-line', text: 'ISO Quality Certified' },
              { icon: 'ri-global-line', text: 'Professional Manufacturing Services' },
              { icon: 'ri-customer-service-2-line', text: 'Dedicated Account Support' },
            ].map((badge) => (
              <div key={badge.text} className="flex items-center justify-center gap-2 sm:gap-3">
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0">
                  <i className={`${badge.icon} text-green-300 text-lg sm:text-xl`} />
                </div>
                <span className="text-white font-medium text-[0.875rem] sm:text-[0.9375rem] whitespace-nowrap">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </EnLayout>
  );
}
